import { Button, Flex, Popover, Tag, Tooltip, Typography } from 'antd'
import { TagOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGetMoneyFlow } from '../../api/case/document.ts'
import { MetadataLabel, useMetadataLabels, useMetadataLabelsBatch } from '../../api/labels/metadataLabels.ts'

// How many labels show inline; the rest are in a popover. A single line
// shows one: table rows and the header have room for one line of tags (the
// table sizes its pages by a fixed row height)
const SHOWN = 2
const SHOWN_SINGLE_LINE = 1

// Addresses whose labels load without a click: the first rows / cards
export const TOP_LABELLED = 5

// Prefetches labels of the given (top) addresses of the current flow in one request
export const usePrefetchAddressLabels = (addresses: string[]) => {
    const { documentId } = useParams()
    const { data: moneyflow } = useGetMoneyFlow(documentId)
    useMetadataLabelsBatch(moneyflow?.network, addresses)
}

// A line of tags or the button is 20px high on a single line, so an address
// with its labels is no taller than the two-line time / amount cells
const SINGLE_LINE_HEIGHT = 20

const labelTag = (label: MetadataLabel, singleLine = false) =>
    <Tooltip key={label.value} title={label.type}>
        <Tag
            color='geekblue'
            style={{
                marginInlineEnd: 0, maxWidth: 136, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                ...(singleLine && { lineHeight: `${SINGLE_LINE_HEIGHT - 2}px` }),
            }}
        >
            {label.value}
        </Tag>
    </Tooltip>

// Bitquery's labels of an address in the current flow. Top addresses get
// theirs prefetched (useMetadataLabelsBatch); any other shows a tag button
// that fetches its labels on click.
export const AddressMetadataLabels = ({ address, singleLine = false }: { address: string, singleLine?: boolean }) => {
    const { documentId } = useParams()
    const { data: moneyflow } = useGetMoneyFlow(documentId)
    const { data: labels, isFetching, isError, fetch } = useMetadataLabels(moneyflow?.network, address)
    const [asked, setAsked] = useState(false)

    if (!moneyflow) return null

    if (labels === undefined) {
        return <Tooltip title={isError ? "Couldn't load labels, click to retry" : 'Show Bitquery labels'}>
            <Button
                size='small'
                type='text'
                danger={isError}
                icon={<TagOutlined />}
                style={singleLine ? { height: SINGLE_LINE_HEIGHT } : undefined}
                loading={isFetching}
                onClick={(e) => {
                    e.stopPropagation()
                    setAsked(true)
                    fetch()
                }}
            />
        </Tooltip>
    }

    if (labels.length === 0) {
        // Only say so when asked for this address (prefetched top addresses stay quiet)
        return asked ? <Typography.Text type='secondary' style={{ fontSize: 12 }}>No labels</Typography.Text> : null
    }

    const shown = singleLine ? SHOWN_SINGLE_LINE : SHOWN
    return <Flex gap={4} wrap={singleLine ? 'nowrap' : 'wrap'} align='center' style={{ maxWidth: '100%', minWidth: 0 }}>
        {labels.slice(0, shown).map((label) => labelTag(label, singleLine))}
        {labels.length > shown && <Popover
            content={<Flex gap={4} wrap='wrap' style={{ maxWidth: 320 }}>{labels.map((label) => labelTag(label))}</Flex>}
        >
            <Tag style={{
                marginInlineEnd: 0, cursor: 'pointer', flex: 'none',
                ...(singleLine && { lineHeight: `${SINGLE_LINE_HEIGHT - 2}px` }),
            }}>+{labels.length - shown}</Tag>
        </Popover>}
    </Flex>
}
